/**
 * Frontier overview — entrega 1 KPIs (REQ-FRT-016 §1) + acción principal
 * «Nuevo agente frontera» (revisión UI del 18-Oct-2026).
 */

import { useState } from 'react';
import { Compass } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useHasRole } from '@/hooks/useRoles';
import { useTenant } from '@/contexts/TenantContext';
import { KpiCard, formatDateTime } from '../components/FrontierGuard';
import { RoutineEditorDialog } from '../components/RoutineEditorDialog';
import { useFrontierSummary, usePlatformSummary } from '../hooks/useFrontier';

export default function FrontierOverviewPage() {
  const { t } = useI18n();
  const canEdit = useModuleAccess('frontier', 'edit');
  const { data, isLoading, isError } = useFrontierSummary();
  const { currentTenant } = useTenant();
  const isPlatformAdmin = useHasRole('admin') && currentTenant?.is_system_tenant === true;
  const platform = usePlatformSummary(isPlatformAdmin);
  const [editorOpen, setEditorOpen] = useState(false);

  return (
    <div className="p-6 space-y-6" data-testid="frontier-overview">
      <PageHeader
        icon={Compass}
        title={t('frontier.title')}
        subtitle={t('frontier.subtitle')}
        actions={
          canEdit ? (
            <Button data-testid="frontier-new-routine" onClick={() => setEditorOpen(true)}>
              + {t('frontier.summary.newRoutine')}
            </Button>
          ) : undefined
        }
        testId="frontier-header"
      />

      {isPlatformAdmin && platform.data && (
        <Card data-testid="frontier-platform">
          <CardContent className="space-y-2 pt-6">
            <h2 className="text-lg font-semibold">{t('frontier.platform.title')}</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('frontier.platform.tenants')}</p>
                <p className="text-xl font-semibold">{platform.data.tenants_with_activity}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('frontier.platform.emergencyStopped')}</p>
                <p className="text-xl font-semibold">{platform.data.emergency_stopped}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('frontier.platform.openInterventions')}</p>
                <p className="text-xl font-semibold">{platform.data.interventions_open}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('frontier.platform.note')}</p>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner size="sm" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-600" role="alert">
          {t('frontier.actions.failed')}
        </p>
      )}

      {data && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <KpiCard
            label={t('frontier.summary.routinesActive')}
            value={data.routines.active ?? 0}
          />
          <KpiCard
            label={t('frontier.summary.routinesPaused')}
            value={(data.routines.paused ?? 0) + (data.routines.auto_paused ?? 0)}
          />
          <KpiCard label={t('frontier.summary.runsQueued')} value={data.runs.queued ?? 0} />
          <KpiCard label={t('frontier.summary.runsRunning')} value={data.runs.running ?? 0} />
          <KpiCard label={t('frontier.summary.runsBlocked')} value={data.runs.blocked ?? 0} />
          <KpiCard label={t('frontier.summary.errors24h')} value={data.errors_24h} />
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('frontier.summary.nextScheduled')}
              </p>
              <p className="mt-1 text-lg font-medium">
                {data.next_scheduled_at
                  ? formatDateTime(data.next_scheduled_at)
                  : t('frontier.summary.none')}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('frontier.summary.title')}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-500 dark:text-gray-400">
          {t('frontier.editor.agentHint')}
        </CardContent>
      </Card>

      <RoutineEditorDialog
        open={editorOpen}
        routineId={null}
        onOpenChange={setEditorOpen}
      />
    </div>
  );
}
